/**
 * « Le Fantôme » — l'écran de la 5e fin ⟦A-58⟧, SPEC-MOTEUR §9.4bis ⟦A-4 décision 1 + F⟧.
 *
 * Les autres fins sont des **accidents** : on se brûle (le burn-out terminal est, depuis le grand
 * ménage ⟦C60 · A-177⟧, la seule autre fin de défaite ; on ne dérape plus, on ne se fait plus bannir
 * et on ne fait plus faillite). Elle a son coup de tonnerre, et le mémorial hardcore dit « Internet
 * t'a oublié » comme une sentence. Le Fantôme n'est pas un accident et §9.4bis lui refuse
 * explicitement ce mémorial : *« pas de mémorial "Internet t'a oublié" mais son propre
 * écran »*. Rien ne s'est passé — c'est bien là le problème.
 *
 * D'où le parti pris : **on ne montre pas une fin, on montre une page**. La chaîne telle qu'un
 * visiteur la trouve des années après, avec la dernière vidéo en haut et, dessous, les
 * commentaires qui continuent d'arriver sans réponse. C'est la fin la plus réaliste du jeu, et
 * la seule qui se raconte au présent.
 *
 * ⟦A-4 décision 17⟧ La variante « jamais publié » porte le message du perfectionnisme : elle
 * ne parle pas d'audience perdue mais de vidéos jamais sorties.
 *
 * ⟦C48 · A-168 §3⟧ **Dans la DA** : une `Fenetre` sur le fond de la chambre (`EcranFenetre.tsx`),
 * le titre du récit dans le bandeau, les deux boutons dans le pied fixe (`pied`, posé par `Fin`).
 * C'est toujours le seul écran de fin décoloré (`.fenetre-eteinte`, saturation à 45 %). Et la
 * page de chaîne « oubliée » a désormais le vrai logo pixel de MeTube pour avatar, à la place de
 * l'emoji télé d'avant.
 *
 * ⟦C57 · lot 7 · A-174⟧ **Rien ne part dans « Les dessous » : c'est une fin** (Matt, 02/10 : la
 * narration et l'épitaphe restent à l'écran, c'est la récompense). Le récit, les chiffres et la page
 * de chaîne « oubliée » (ses commentaires sont de la fiction) restent tels quels ; les blocs de
 * narration portent `data-narration`, pour que la mesure de conformité les range à part. Seul
 * changement : « il y a 1 jours » (une journée de silence) s'accorde. L'épitaphe courte du Fantôme
 * (`RECITS_DE_FIN.fantome`, `game/fins.ts`) est celle que le Journal garde.
 *
 * ⟦C60 · A-177⟧ **Rien ici ne dépendait de l'argent** : le Fantôme naît d'un silence, de vidéos nulles
 * et d'une rente à zéro (SPEC-MOTEUR §9.4bis), jamais d'un loyer, d'une charge ou d'une faillite. Les
 * trois récits et la page « oubliée » restent donc tels quels ; seul ce commentaire a changé.
 */

import type { ReactNode } from "react";

import { EcranFenetre } from "../../components/EcranFenetre";
import { LogoMeTube } from "../../components/LogoMeTube";
import { jourSimule, nombre } from "../../game/format";

/** Les trois visages de l'extinction — c'est l'état qui les départage, pas un tirage. */
export type VarianteFantome = "jamais_publie" | "jamais_decolle" | "extinction";

/**
 * ⟦A-4 F⟧ Deux voies au moteur, **trois** récits ici : la voie « jamais décollé » se scinde
 * selon qu'une vidéo est sortie ou non. C'est la seule distinction que le joueur ressent —
 * n'avoir jamais osé publier et avoir cessé de publier ne sont pas la même histoire.
 */
export function varianteFantome(videosPubliees: number, joursDeSilence: number): VarianteFantome {
  if (videosPubliees === 0) return "jamais_publie";
  return joursDeSilence >= 60 ? "jamais_decolle" : "extinction";
}

const RECITS: Record<VarianteFantome, { titre: string; texte: string; commentaires: string[] }> = {
  jamais_publie: {
    titre: "Zéro vidéo",
    texte:
      "La chaîne existe. Le nom est réservé, la bannière est faite, le matériel est là. Il manquait juste une vidéo, et elle n'était jamais tout à fait prête. Fait vaut mieux que parfait — personne n'a jamais vu ton montage parfait.",
    commentaires: [
      "je suis abonné depuis 3 ans, toujours rien 😭",
      "il attend quoi en fait",
      "la meilleure vidéo, c'est celle qui sort",
    ],
  },
  jamais_decolle: {
    titre: "Il est où ?",
    texte:
      "Tu n'as jamais annoncé d'arrêt. Tu n'as jamais fait de vidéo bilan. Un jour tu n'as pas publié, et le lendemain non plus. Les gens ont mis des mois à s'en rendre compte — c'est comme ça que la plupart des chaînes finissent.",
    commentaires: [
      "il est où ?",
      "qui est là en 2031",
      "j'espère qu'il va bien, sincèrement",
    ],
  },
  extinction: {
    titre: "La chaîne s'éteint doucement",
    texte:
      "Tu as continué. Une vidéo, puis une autre, puis encore une — et plus personne en face. Les vues sont tombées sous le seuil où l'algorithme regarde encore, la rente s'est tarie, et le silence a fait le reste. Il n'y a pas eu de drame : c'est ça, le drame.",
    commentaires: [
      "l'algo l'a tué",
      "ses vieilles vidéos étaient bien pourtant",
      "il est où ?",
    ],
  },
};

export interface FantomeProps {
  nomChaine: string;
  abonnes: number;
  videosPubliees: number;
  clockH: number;
  /** `clock_h` de la dernière publication — `null` si aucune vidéo n'est jamais sortie. */
  dernierePublicationH: number | null;
  /** Titre de la dernière vidéo publiée, quand il y en a une. */
  derniereVideo: string | null;
  /** Les boutons d'action (Nouvelle chaîne, Historique), posés dans le pied de la fenêtre. */
  pied?: ReactNode;
}

/** L'écran d'extinction : une page de chaîne consultée des années plus tard. */
export function Fantome(props: FantomeProps) {
  const joursDeSilence =
    props.dernierePublicationH === null
      ? jourSimule(props.clockH)
      : jourSimule(props.clockH) - jourSimule(props.dernierePublicationH);
  const variante = varianteFantome(props.videosPubliees, joursDeSilence);
  const recit = RECITS[variante];

  return (
    <EcranFenetre titre={recit.titre} icone="👻" className="fenetre-eteinte" pied={props.pied}>
      <p data-narration="fin">{recit.texte}</p>
      <p className="ligne-info discret">
        {nombre(props.abonnes)} abonnés · {props.videosPubliees} vidéos · jour{" "}
        {jourSimule(props.clockH)}
      </p>

      {/* La page de chaîne telle qu'on la trouve. Pas une pierre tombale : un onglet ouvert
          par hasard, des années après, et qui n'a jamais été fermé. */}
      <div className="chaine-eteinte" data-narration="fin">
        <div className="eteinte-entete">
          <div className="eteinte-avatar" aria-hidden="true">
            <LogoMeTube className="eteinte-logo" />
          </div>
          <div>
            <p className="eteinte-nom">{props.nomChaine}</p>
            <p className="eteinte-meta">
              {nombre(props.abonnes)} abonnés ·{" "}
              {props.dernierePublicationH === null
                ? "aucune vidéo"
                : `dernière vidéo il y a ${nombre(joursDeSilence)} ${joursDeSilence > 1 ? "jours" : "jour"}`}
            </p>
          </div>
        </div>

        {props.derniereVideo ? (
          <p className="eteinte-derniere">« {props.derniereVideo} »</p>
        ) : (
          <p className="eteinte-derniere eteinte-vide">Cette chaîne n'a mis aucune vidéo en ligne.</p>
        )}

        <ul className="eteinte-commentaires">
          {recit.commentaires.map((c) => (
            <li key={c}>
              <span className="eteinte-pseudo">@</span> {c}
            </li>
          ))}
        </ul>
      </div>
    </EcranFenetre>
  );
}
